/**
 * MifeLife v2 — Mentor onboarding wizard (/mentor/onboarding).
 * 7 steps: Profil → Uzmanlık → Deneyim → Eğitim → Fiyat → Müsaitlik → IBAN.
 * Each "Devam" saves the step via PATCH /api/v2/mentor/me. Final step marks
 * onboarding complete and routes to the panel. Live completion score shown.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, ArrowRight, ArrowLeft, Check } from 'lucide-react';
import { apiRequest, getToken } from '../../lib/utils';
import { useNoIndex } from '../../hooks/useNoIndex';
import {
  SPECIALTY_OPTIONS, LANG_OPTIONS, Field, TextInput, TextArea, NumberInput,
  ChipMultiSelect, ListEditor, PricingEditor, AvailabilityEditor, CompletionBar, PhotoUpload,
} from '../../components/v2/MentorEditorFields';

const STEPS = [
  { key: 'profil', label: 'Profil', fields: ['name', 'title', 'photo_url', 'city', 'languages', 'bio_short', 'bio_full', 'session_about', 'linkedin_url', 'website_url'] },
  { key: 'uzmanlik', label: 'Uzmanlık', fields: ['specialties'] },
  { key: 'deneyim', label: 'Deneyim', fields: ['experience'] },
  { key: 'egitim', label: 'Eğitim', fields: ['education'] },
  { key: 'fiyat', label: 'Fiyat', fields: ['pricing', 'hourly_rate_try'] },
  { key: 'musaitlik', label: 'Müsaitlik', fields: ['available_hours'] },
  { key: 'odeme', label: 'IBAN & Vergi', fields: ['iban', 'tax_no'] },
];

export default function V2MentorOnboardingPage() {
  useNoIndex();
  const nav = useNavigate();
  const [form, setForm] = useState(null);
  const [completion, setCompletion] = useState(0);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!getToken()) { nav('/mentor/giris'); return; }
    apiRequest('/v2/mentor/me')
      .then((m) => { setForm(m); setCompletion(m.profile_completion ?? 0); })
      .catch(() => nav('/mentor/panel'));
  }, [nav]);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const saveStep = async () => {
    setBusy(true);
    try {
      const payload = {};
      STEPS[step].fields.forEach((k) => { if (form[k] !== undefined) payload[k] = form[k]; });
      // Only PATCH when this step actually has data — empty steps just advance.
      if (Object.keys(payload).length > 0) {
        const updated = await apiRequest('/v2/mentor/me', { method: 'PATCH', body: JSON.stringify(payload) });
        setForm((f) => ({ ...f, ...updated }));
        setCompletion(updated.profile_completion ?? completion);
      }
      if (step < STEPS.length - 1) {
        setStep(step + 1);
      } else {
        await apiRequest('/v2/mentor/onboarding/complete', { method: 'POST' });
        toast.success('Onboarding tamamlandı! 🎉');
        nav('/mentor/panel');
      }
    } catch (e) {
      toast.error(e?.message || 'Kaydedilemedi');
    } finally { setBusy(false); }
  };

  if (!form) {
    return <div className="min-h-screen bg-[#0F0F1A] flex items-center justify-center"><Loader2 className="w-7 h-7 text-violet-300 animate-spin" /></div>;
  }

  const cur = STEPS[step];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0F0F1A] via-[#11111E] to-[#1A1A2E] text-white antialiased" data-testid="v2-mentor-onboarding">
      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-12">
        <div className="text-[11px] uppercase tracking-[0.18em] text-violet-300 mb-2">Mentör onboarding · Adım {step + 1}/{STEPS.length}</div>
        <h1 className="text-3xl font-semibold tracking-tight mb-4">{cur.label}</h1>

        {/* Step dots */}
        <div className="flex items-center gap-1.5 mb-6">
          {STEPS.map((s, i) => (
            <div key={s.key} className={`h-1.5 flex-1 rounded-full transition-all ${i < step ? 'bg-violet-500' : i === step ? 'bg-violet-400' : 'bg-white/10'}`} data-testid={`v2-onb-dot-${i}`} />
          ))}
        </div>
        <div className="mb-8"><CompletionBar value={completion} /></div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 space-y-5" data-testid={`v2-onb-step-${cur.key}`}>
          {cur.key === 'profil' && (
            <>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Ad Soyad"><TextInput value={form.name} onChange={(v) => set('name', v)} testid="onb-name" /></Field>
                <Field label="Ünvan"><TextInput value={form.title} onChange={(v) => set('title', v)} placeholder="Senior Ürün Mentoru" testid="onb-title" /></Field>
                <Field label="Şehir"><TextInput value={form.city} onChange={(v) => set('city', v)} placeholder="İstanbul" testid="onb-city" /></Field>
                <Field label="Profil fotoğrafı"><PhotoUpload value={form.photo_url} onChange={(v) => set('photo_url', v)} testidPrefix="onb-photo" /></Field>
                <Field label="LinkedIn URL"><TextInput value={form.linkedin_url} onChange={(v) => set('linkedin_url', v)} placeholder="https://linkedin.com/in/..." testid="onb-linkedin" /></Field>
                <Field label="Website"><TextInput value={form.website_url} onChange={(v) => set('website_url', v)} placeholder="https://..." testid="onb-website" /></Field>
              </div>
              <Field label="Diller"><ChipMultiSelect options={LANG_OPTIONS} selected={form.languages || []} onChange={(v) => set('languages', v)} getKey={(o) => o.code} getLabel={(o) => o.label} testidPrefix="onb-lang" /></Field>
              <Field label="Kısa biyografi"><TextArea value={form.bio_short} onChange={(v) => set('bio_short', v)} rows={2} testid="onb-bio-short" /></Field>
              <Field label="Detaylı biyografi"><TextArea value={form.bio_full} onChange={(v) => set('bio_full', v)} rows={5} testid="onb-bio-full" /></Field>
              <Field label="Görüşmede ne konuşulur?"><TextArea value={form.session_about} onChange={(v) => set('session_about', v)} rows={3} testid="onb-session-about" /></Field>
            </>
          )}
          {cur.key === 'uzmanlik' && (
            <Field label="Uzmanlık alanların (2+ önerilir)"><ChipMultiSelect options={SPECIALTY_OPTIONS} selected={form.specialties || []} onChange={(v) => set('specialties', v)} testidPrefix="onb-spec" /></Field>
          )}
          {cur.key === 'deneyim' && (
            <ListEditor items={form.experience || []} onChange={(v) => set('experience', v)} addLabel="Deneyim ekle" testid="onb-experience"
              fields={[{ key: 'company', placeholder: 'Şirket' }, { key: 'role', placeholder: 'Pozisyon' }, { key: 'period', placeholder: '2018 - Bugün' }]} />
          )}
          {cur.key === 'egitim' && (
            <ListEditor items={form.education || []} onChange={(v) => set('education', v)} addLabel="Eğitim ekle" testid="onb-education"
              fields={[{ key: 'school', placeholder: 'Okul' }, { key: 'degree', placeholder: 'Derece / Bölüm' }, { key: 'period', placeholder: '2010 - 2014' }]} />
          )}
          {cur.key === 'fiyat' && (
            <>
              <Field label="Saatlik referans ücret (₺)"><NumberInput value={form.hourly_rate_try} onChange={(v) => set('hourly_rate_try', v)} testid="onb-hourly" /></Field>
              <Field label="Süreye göre fiyatını belirle"><PricingEditor pricing={form.pricing || []} onChange={(v) => set('pricing', v)} /></Field>
            </>
          )}
          {cur.key === 'musaitlik' && (
            <AvailabilityEditor availableHours={form.available_hours || {}} onChange={(v) => set('available_hours', v)} />
          )}
          {cur.key === 'odeme' && (
            <>
              <p className="text-xs text-slate-500">Kazançların bu IBAN'a ödenir; yalnızca yönetim görür.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="IBAN"><TextInput value={form.iban} onChange={(v) => set('iban', v)} placeholder="TR.." testid="onb-iban" /></Field>
                <Field label="TC Kimlik / Vergi No"><TextInput value={form.tax_no} onChange={(v) => set('tax_no', v)} testid="onb-tax" /></Field>
              </div>
            </>
          )}
        </div>

        <div className="mt-6 flex items-center justify-between">
          <button
            onClick={() => setStep(Math.max(0, step - 1))}
            disabled={step === 0}
            className="inline-flex items-center gap-1.5 h-11 px-4 rounded-lg border border-white/10 text-slate-300 text-sm hover:bg-white/[0.04] disabled:opacity-40 transition"
            data-testid="v2-onb-back"
          >
            <ArrowLeft className="w-4 h-4" /> Geri
          </button>
          <button
            onClick={() => nav('/mentor/panel')}
            className="text-xs text-slate-500 hover:text-slate-300 transition"
            data-testid="v2-onb-skip"
          >
            Sonra tamamla
          </button>
          <button
            onClick={saveStep}
            disabled={busy}
            className="inline-flex items-center gap-1.5 h-11 px-5 rounded-lg bg-gradient-to-r from-violet-500 to-violet-600 text-white text-sm font-semibold hover:from-violet-400 hover:to-violet-500 disabled:opacity-50 transition"
            data-testid="v2-onb-next"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (step === STEPS.length - 1 ? <Check className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />)}
            {step === STEPS.length - 1 ? 'Tamamla' : 'Devam'}
          </button>
        </div>
      </div>
    </div>
  );
}
